import { toast } from '@side/pantry';
import { useEffect, useMemo } from 'react';
import { formatDisplayName } from '@/common/utils/formatDisplayName';
import { formatTeamMemberIds } from '@/common/utils/formatTeamMemberId';
import { useIdentity } from '$api-clients/hooks';
import {
  CONTACT_SUGGESTION_GROUPS,
  type ContactSuggestion,
  contactSuggestionGroupRank,
  mapContactSuggestionToTeamMembershipIdentifiers,
} from '$contact-suggestions';

/**
 * Merges a selected option that is absent from the current suggestions back
 * into the list without breaking group contiguity. The sort is stable and
 * compares group rank only, so every fetched option keeps its position and
 * the selected option lands at the end of its group's block (or at the
 * canonical position for its group when the group has no other results).
 */
export function mergeSelectedOption(
  options: ContactSuggestion[],
  selectedOption: ContactSuggestion,
): ContactSuggestion[] {
  return [...options, selectedOption].sort(
    (a, b) =>
      contactSuggestionGroupRank(a.group) - contactSuggestionGroupRank(b.group),
  );
}

function getOptionIds(option: ContactSuggestion): string[] | null {
  if (option.userId) {
    return formatTeamMemberIds(
      mapContactSuggestionToTeamMembershipIdentifiers(option),
    );
  }
  if (option.googleId) return [option.googleId];
  if (option.followUpBossId) return [option.followUpBossId];

  return null;
}

export function isOptionEqualToValue(
  option: ContactSuggestion,
  value: ContactSuggestion | string | null,
) {
  if (!value || typeof value === 'string') {
    return false;
  }

  const optionIds = getOptionIds(option);
  const valueIds = getOptionIds(value);

  // If either ID is null, they're not reliably comparable
  if (optionIds === null || valueIds === null) {
    return false;
  }

  return optionIds.some((optionId) => valueIds.includes(optionId));
}

interface UseDefaultContactSuggestionOptions {
  userId: string;
  teamId: string;
  userTeamId: string;
}

interface UseDefaultContactSuggestionReturn {
  defaultValue: ContactSuggestion | undefined;
  loading: boolean;
}

export function useDefaultContactSuggestion({
  userId,
  teamId,
  userTeamId,
}: UseDefaultContactSuggestionOptions): UseDefaultContactSuggestionReturn {
  const fetchData = !!userId && !!teamId;

  const {
    data: team,
    error: teamError,
    isLoading: teamLoading,
  } = useIdentity(
    '/teams/{teamId}',
    fetchData ? { params: { path: { teamId } } } : null,
  );

  const {
    data: member,
    error: memberError,
    isLoading: memberLoading,
  } = useIdentity(
    '/teams/{teamId}/members/{userId}',
    fetchData ? { params: { path: { userId, teamId } } } : null,
  );

  const defaultValue: ContactSuggestion | undefined = useMemo(() => {
    if (!member || !team) return undefined;

    const group =
      team.id === userTeamId || team.legacyExternalId === userTeamId
        ? CONTACT_SUGGESTION_GROUPS.teamContacts
        : CONTACT_SUGGESTION_GROUPS.otherContacts;

    return {
      userId: member.userId,
      googleId: null,
      followUpBossId: null,
      firstName: member.firstName,
      lastName: member.lastName,
      displayName: formatDisplayName({
        firstName: member.firstName,
        lastName: member.lastName,
        teamName: team.legalName ?? undefined,
        teamAlias: team.alias,
      }),
      email: member.contact.email,
      photoUrl: member.photoUrl ?? null,
      contactSource: null,
      contactResponse: {
        // `legacyExternalId` can come back as "" (not null), so use `||` to
        // fall back to the identity UUID rather than keep a blank id.
        userId: member.legacyExternalId || member.userId,
        teamId: team.legacyExternalId || team.id,
        userIdentityId: member.userId,
        teamIdentityId: team.id,
      },
      group,
    };
  }, [team, member, userTeamId]);

  const hasError = !!teamError || !!memberError;

  useEffect(() => {
    if (hasError) {
      toast('error', 'Could not find agent. Please refresh and try again.');
    }
  }, [hasError]);

  return {
    defaultValue,
    loading: teamLoading || memberLoading,
  };
}
